import React from 'react';
import { ExternalLink, Github } from 'lucide-react';
import '../styles/projects.css';

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: 'Reflectify',
      description: 'An AI-powered mobile application designed to combat loneliness among the elderly through intelligent companionship and engagement features.',
      technologies: ['Dart', 'Flutter', 'Android Studio', 'AI/ML', 'HTML', 'CSS', 'Website Design'],
      role: 'Co-founder & Developer',
      highlights: [
        'Developed Android mobile app functionality',
        'Created promotional website with HTML/CSS',
        'Integrated AI-powered features for user engagement',
        'Reached Innovation Labs semifinal'
      ],
      links: {
        github: 'https://github.com/Iannis863/Reflectify-Website'
      },
      image: '/reflectify-team.jpg'
    },
    {
      id: 2,
      title: 'Chess AI Application',
      description: 'An interactive chess application featuring AI opponent gameplay and post-game analysis tools to help players improve their strategic thinking and chess skills.',
      technologies: ['Python', 'AI/ML', 'Game Development', 'Chess Algorithms'],
      role: 'Developer',
      highlights: [
        'Implemented AI opponent with adjustable difficulty',
        'Built match analysis system for game review',
        'Created intuitive chess board interface',
        'Developed move suggestion and improvement features'
      ],
      links: {
        github: 'https://github.com/Iannis863/ChessApp'
      },
      image: '/chess-app.png'
    },
    {
      id: 3,
      title: 'TransylvaniaTrivia',
      description: 'A weekly quiz event with automated slide generation. Built a comprehensive website and Python automation tool that streamlines quiz creation and presentation.',
      technologies: ['Python', 'TypeScript', 'Automation', 'Event Management'],
      role: 'Co-Founder & Developer',
      highlights: [
        'Co-founded and manage weekly quiz events',
        'Developed event website for registration and information',
        'Created Python automation app for slide generation',
        'Automated quiz presentation workflow'
      ],
      links: {
        github: 'https://github.com/Iannis863/TransylvaniaTriviaApp',
        githubLabel: 'App Code',
        demo: 'https://github.com/Iannis863/TransylvaniaTriviaWebsite',
        demoLabel: 'Website Code'
      },
      website: 'https://transylvaniatrivia.com'
    },
    {
      id: 4,
      title: 'AI Academy',
      description: 'A Duolingo-style web application for learning Artificial Intelligence, making complex AI concepts accessible through interactive lessons and gamification.',
      technologies: ['React', 'TypeScript', 'Python', 'Machine Learning'],
      role: 'Founder & Lead Developer',
      highlights: [
        'Built interactive learning platform',
        'Implemented gamification mechanics',
        'Created AI course curriculum'
      ],
      links: {
        github: 'https://github.com/Iannis863/AI-Academy'
      }
    },
    {
      id: 5,
      title: 'Database Management System',
      description: 'Academic project focusing on advanced database design, optimization, and implementation using SQL and modern database technologies.',
      technologies: ['SQL', 'Database Design'],
      role: 'Developer',
      highlights: [
        'Designed normalized database schema',
        'Implemented complex queries and procedures',
        'Earned HackerRank Database Certificate'
      ],
      links: {
        github: 'https://github.com/Iannis863/Atestat'
      }
    }
  ];

  return (
    <section id="projects" className="projects-section">
      <div className="projects-container">
        <div className="section-header">
          <h2 className="section-title">Featured Projects</h2>
          <div className="section-divider"></div>
          <p className="section-description">
            A selection of projects that showcase my technical skills and passion for innovation
          </p>
        </div>

        <div className="projects-grid">
          {projects.map((project) => {
            const CardWrapper = project.website ? 'a' : 'div';
            const wrapperProps = project.website
              ? {
                  href: project.website,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  className: 'project-card project-card-clickable'
                }
              : { className: 'project-card' };

            return (
              <CardWrapper key={project.id} {...wrapperProps}>
                {project.image && (
                  <div className="project-image">
                    <img src={project.image} alt={project.title} />
                  </div>
                )}
                
                <div className="project-header">
                  <h3 className="project-title">{project.title}</h3>
                  <span className="project-role">{project.role}</span>
                </div>

                <p className="project-description">{project.description}</p>

                <div className="project-highlights">
                  <h4 className="highlights-title">Key Achievements:</h4>
                  <ul className="highlights-list">
                    {project.highlights.map((highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div className="project-technologies">
                  {project.technologies.map((tech, index) => (
                    <span key={index} className="tech-tag">{tech}</span>
                  ))}
                </div>

                <div className="project-links" onClick={(e) => e.stopPropagation()}>
                  {project.links.github && (
                    <a href={project.links.github} className="project-link" target="_blank" rel="noopener noreferrer">
                      <Github size={18} /> {project.links.githubLabel || 'Code'}
                    </a>
                  )}
                  {project.links.demo && (
                    <a href={project.links.demo} className="project-link" target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={18} /> {project.links.demoLabel || 'Demo'}
                    </a>
                  )}
                </div>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects;